import { trackEvent } from '../util/analytics'

// Audio player
// Use `render_audio_for_text` to place hidden <audio> elements inside of it.
// Controlled by start/stop buttons that need to be placed inside of it.
// Will play all audios inside of it, one after the other.
// Autoplays if audio has not been manually stopped with the stop button.
//
up.compiler('[read-aloud]', async (element, { autoPlay, saveSettingUrl, setPagePlayedUrl }) => {

  const TRACK_PAGE_READ_ALOUD_COUNTS = [2, 5]

  const startButtonSelector = '[read-aloud--start-button]'
  const stopButtonSelector = '[read-aloud--stop-button]'

  const audioPlayingClass = 'audio-playing'
  const highlightClass = 'highlight'

  let audios
  let audiosByPlaybackIndex
  let playedAnything
  let playing
  let allAudiosPlayed
  let userPressedStop
  let playCountAfterFirstStop
  let playCountAfterFirstFinish
  let currentAudio

  init()

  up.on(element, 'click', startButtonSelector, onClickButton)
  up.on(element, 'click', stopButtonSelector, onClickButton)

  function init() {
    if (currentAudio) stopAudio(currentAudio)

    audios = element.querySelectorAll('audio')
    audiosByPlaybackIndex = Object.fromEntries([...audios].map(audio => [audio.dataset.playbackIndex, audio]))

    playedAnything = false
    playing = false
    allAudiosPlayed = false
    userPressedStop = false
    playCountAfterFirstStop = 0
    playCountAfterFirstFinish = 0
    currentAudio = undefined

    updateVisibleButtonText()

    if (audios.length < 1) {
      console.warn('[read-aloud] No audio elements found.')
    } else {
      trackEvent('read-aloud', 'readable-content-present')

      audios.forEach((audio) => {
        audio.addEventListener('ended', () => {
          onAudioEnded(audio)
        })
      })

      removeHighlights()
      tryAutoPlay()
    }
  }

  async function tryAutoPlay() {
    if (autoPlay && audios.length > 0) {
      preloadAudios()

      try {
        await play({ throwError: true })
        trackEvent('read-aloud', 'auto-play')
      } catch {
        // Autoplay was prevented
        trackEvent('read-aloud', 'auto-play-prevented')
      }
    }
  }

  function isPlayable(audio) {
    return up.element.isVisible(audio.parentElement)
  }

  function onAnyPlaybackEnded(event, playedElement) {
    const { playbackIndex } = playedElement.dataset

    if (playedElement === audios[audios.length - 1]) {
      playing = false
      allAudiosPlayed = true
      updateVisibleButtonText()
    }

    if (playbackIndex) {
      const nextIndex = `${parseInt(playbackIndex) + 1}`
      const nextAudio = audiosByPlaybackIndex[nextIndex]

      if (!nextAudio) return

      if (playing && isPlayable(nextAudio)) {
        playAudio(nextAudio)
      } else {
        // Next element in line cannot be played because its container is hidden.
        // We skip over it so the potentially next (or later) element can play.
        up.emit(nextAudio, 'app:playback:ended')
      }
    }
  }

  function onPlaybackContentRevealed({ elements }) {
    if (!playedAnything) return

    const revealedAudio = [...audios].find(audio => [...elements].some(el => el.contains(audio)))

    if (revealedAudio && isPlayable(revealedAudio)) {
      playAudio(revealedAudio, { throwError: true })
        .catch(up.util.noop) // Ignore play errors. Those happen when reloading a solved slide and can be ignored.
    }
  }

  async function onClickButton() {
    if (audios.length < 1) {
      showNoAudiosDialog()
      return
    }

    const requestedPlayback = !playing

    if (requestedPlayback) {
      preloadAudios()

      await play()
      trackEvent('read-aloud', 'click-play')
      if (allAudiosPlayed && playCountAfterFirstFinish === 1) {
        trackEvent('read-aloud', 'play-again-after-reading-finished')
      } else if (userPressedStop && playCountAfterFirstStop === 1) {
        trackEvent('read-aloud', 'play-again-after-stopping')
      }
    } else if (currentAudio) {
      stopAudio(currentAudio)
      trackEvent('read-aloud', 'click-stop')
      playedAnything = false // clicking stop reverts to "not played anything yet"
      playing = false
      userPressedStop = true
      updateVisibleButtonText()
    }

    saveAutoPlaySetting(requestedPlayback)
  }

  function preloadAudios() {
    audios.forEach((audio) => { audio.preload = 'auto' })
  }

  async function onAudioEnded(audio) {
    const waitMs = audio.dataset.waitAfter
    if (waitMs) {
      await new Promise((resolve, reject) => {
        up.util.timer(waitMs, resolve)
      })
    }
    stopAudio(audio)

    up.emit(audio, 'app:playback:ended')
  }

  function updateVisibleButtonText() {
    element.querySelectorAll(startButtonSelector).forEach((button) => {
      button.hidden = playing
      button.style.opacity = audios.length < 1 ? '0.5' : ''
    })
    element.querySelectorAll(stopButtonSelector).forEach((button) => { button.hidden = !playing })
  }

  function removeHighlights() {
    const highlightedElements = element.querySelectorAll(`[read-aloud--highlight].${highlightClass}`)
    highlightedElements.forEach((element) => { element.classList.remove(highlightClass) })
  }

  async function play({ throwError = false } = {}) {
    if (!isPlayable(audios[0])) return

    trackPlayCountAfterFirstStop()
    trackPlayCountAfterFirstFinish()

    const played = await playAudio(audios[0], { throwError })

    if (played && setPagePlayedUrl) {
      up.request(setPagePlayedUrl, { method: 'PATCH' })
        .then(afterSetPagePlayed)
    }
  }

  async function playAudio(audio, { throwError = false } = {}) {
    if (currentAudio) {
      // avoid playing multiple audios at the same time
      stopAudio(currentAudio)
    }

    if (!audio.isConnected) {
      return
    }

    audio.currentTime = 0

    try {
      await audio.play()
      playing = true
      playedAnything = true

    } catch (error) {
      playing = false

      if (throwError) {
        throw error
      } else {
        console.error(error.message)
        showPlayErrorDialog()
        trackEvent('read-aloud', 'play-error', error.message)
      }
    }

    setPlaying(audio, true)
    currentAudio = audio

    updateVisibleButtonText()

    return playing
  }

  function stopAudio(audio) {
    audio.pause()
    audio.currentTime = 0
    setPlaying(audio, false)
    currentAudio = undefined
  }

  function setPlaying(audio, isPlaying) {
    audio.classList.toggle(audioPlayingClass, isPlaying)

    let container = audio.closest('[read-aloud--highlight]')
    container ??= element.querySelector(`#${audio.getAttribute('for')}`)
    if (container) {
      window.requestAnimationFrame(() => {
        container.classList.toggle(highlightClass, isPlaying)
      })
    }
  }

  function saveAutoPlaySetting(autoPlayEnabled) {
    autoPlay = autoPlayEnabled
    up.request(saveSettingUrl, { method: 'PATCH', params: { read_aloud: autoPlayEnabled } })
  }

  function trackPlayCountAfterFirstStop() {
    if (userPressedStop) {
      playCountAfterFirstStop++
    }
  }

  function trackPlayCountAfterFirstFinish() {
    if (allAudiosPlayed) {
      playCountAfterFirstFinish++
    }
  }

  function afterSetPagePlayed(response) {
    const { count, changed } = response.json
    if (changed && TRACK_PAGE_READ_ALOUD_COUNTS.includes(count)) {
      trackEvent('read-aloud', 'pages-read-aloud-count', count)
    }
  }

  function showNoAudiosDialog() {
    up.layer.open({
      content: `
        <h2>Hmmm…</h2>
        <p>
          Leider kann ich hier nichts vorlesen.
          <br>
          Versuche es auf einer anderen Folie.
        </p>
        <p class="text-end mt-4">
          <button class="btn btn-info" type="button" up-dismiss>Verstanden</button>
        </p>
      `,
      size: 'auto',
    })
  }

  function showPlayErrorDialog() {
    up.layer.open({
      content: `
        <h2 class="text-danger">Hoppla!</h2>
        <p>Leider funktioniert das Vorlesen auf deinem Gerät nicht richtig.</p>
        <p>Bitte überprüfe deine Internetverbindung und Browser-Einstellungen.</p>
        <p class="text-end mt-4">
          <button class="btn btn-info" type="button" up-dismiss>Verstanden</button>
        </p>
      `,
      size: 'auto',
    })
  }

  return [
    up.on('app:playback:ended', onAnyPlaybackEnded),
    up.on('app:playback:revealed', onPlaybackContentRevealed),
    up.on('app:translation:updated', init),
    () => { if (currentAudio) stopAudio(currentAudio) },
  ]

})
